import { useCallback, useEffect, useMemo, useState } from 'react';

import { useLocation } from 'react-router';
import { useHistory } from 'react-router-dom';

import { CruiseInclusion } from '~/components/Cruises/pages/Inclusions/types';

import inclusionsService from '~/services/cruises/InclusionsService';

import { arrayToParam, paramToArray, parseSearchString, stringifyQuery } from '~/utils/url';

export interface InclusionFilters {
  name?: string;
  rateCodes?: string[];
  vendorCodes?: string[];
  departures?: string[];
}

export type InclusionListSortField = 'startDate' | 'endDate';
export type InclusionListSortDirection = 'asc' | 'desc';

export type InclusionListSort = {
  field: InclusionListSortField;
  direction: InclusionListSortDirection;
};

interface CruisesData {
  inclusions: Array<CruiseInclusion>;
  page: number;
  total: number;
  isLoading: boolean;
  recordsPerPage: number;
  filters: InclusionFilters;
  sort: InclusionListSort | undefined;
  activeTab: string;
  setFilters: (filters: InclusionFilters) => void;
  setActiveTab: (tab: string) => void;
  setSort: (sort: InclusionListSort | undefined) => void;
  paginate: (page: number) => void;
  getInclusionList: () => Promise<void>;
}

const RECORDS_PER_PAGE = 15;

export default function useCruiseInclusions(): CruisesData {
  const { search } = useLocation();
  const { push: setQueryString } = useHistory();
  const [total, setTotal] = useState(0);
  const [inclusions, setInclusions] = useState<Array<CruiseInclusion>>([]);
  const [isLoading, setIsLoading] = useState(false);

  const queryParams = useMemo(() => parseSearchString(search), [search]);

  const page = parseInt(queryParams.page as string) || 1;
  const activeTab = (queryParams.activeTab as string) || 'live';

  const filters: InclusionFilters = useMemo(
    () => ({
      name: (queryParams.name as string) || undefined,
      rateCodes: paramToArray(queryParams.rateCodes as string),
      vendorCodes: paramToArray(queryParams.vendorCodes as string),
      departures: paramToArray(queryParams.departures as string),
    }),
    [queryParams.name, queryParams.rateCodes, queryParams.vendorCodes, queryParams.departures],
  );

  const sort: InclusionListSort | undefined = useMemo(() => {
    const field = queryParams.sortBy as string | undefined;
    const direction = queryParams.sortDirection as string | undefined;
    if ((field === 'startDate' || field === 'endDate') && (direction === 'asc' || direction === 'desc')) {
      return { field, direction };
    }
    return undefined;
  }, [queryParams.sortBy, queryParams.sortDirection]);

  const currentUrlParams = useCallback(
    (overrides: Record<string, string | undefined>) =>
      stringifyQuery({
        page: page.toString(),
        activeTab: activeTab.toString(),
        name: filters.name,
        rateCodes: arrayToParam(filters.rateCodes),
        vendorCodes: arrayToParam(filters.vendorCodes),
        departures: arrayToParam(filters.departures),
        ...(sort ? { sortBy: sort.field, sortDirection: sort.direction } : {}),
        ...overrides,
      }),
    [page, activeTab, filters, sort],
  );

  const setFilters = useCallback(
    (newFilters: InclusionFilters) => {
      setQueryString({
        search: currentUrlParams({
          page: '1',
          name: newFilters.name,
          rateCodes: arrayToParam(newFilters.rateCodes),
          vendorCodes: arrayToParam(newFilters.vendorCodes),
          departures: arrayToParam(newFilters.departures),
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const setActiveTab = useCallback(
    (tab: string) => {
      setQueryString({
        search: currentUrlParams({
          page: '1',
          activeTab: tab,
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const setSort = useCallback(
    (nextSort: InclusionListSort | undefined) => {
      setQueryString({
        search: stringifyQuery({
          page: '1',
          activeTab: activeTab.toString(),
          name: filters.name,
          rateCodes: arrayToParam(filters.rateCodes),
          vendorCodes: arrayToParam(filters.vendorCodes),
          departures: arrayToParam(filters.departures),
          ...(nextSort ? { sortBy: nextSort.field, sortDirection: nextSort.direction } : {}),
        }),
      });
    },
    [setQueryString, activeTab, filters],
  );

  const paginate = useCallback(
    (page: number) => {
      setQueryString({
        search: currentUrlParams({
          page: page.toString(),
        }),
      });
    },
    [setQueryString, currentUrlParams],
  );

  const getInclusionList = useCallback(async () => {
    try {
      const skip = (page - 1) * RECORDS_PER_PAGE;
      const isActive = activeTab === 'live';
      setIsLoading(true);
      const res = await inclusionsService.listWithPagination({
        skip,
        take: RECORDS_PER_PAGE,
        isActive,
        ...filters,
        ...(sort ? { sortBy: sort.field, sortDirection: sort.direction } : {}),
      });
      setInclusions(res.result);
      setTotal(res.total);
      setIsLoading(false);
    } catch (error) {
      setIsLoading(false);
    }
  }, [page, activeTab, filters, sort]);

  useEffect(() => {
    getInclusionList();
  }, [getInclusionList]);

  return {
    inclusions,
    getInclusionList,
    page,
    recordsPerPage: RECORDS_PER_PAGE,
    filters,
    sort,
    activeTab,
    setFilters,
    setActiveTab,
    setSort,
    paginate,
    total,
    isLoading,
  };
}
